import { z } from "zod";
import { FORM_ERROR_MESSAGES, IS_VALID_EMAIL, TYPE_EMPLOYMENT, USER_STATUS } from "@shared/constants";
import { Role, User } from "@shared/models";
import { Option } from "@shared/interfaces";
import { actions as sharedActions, selectors as sharedSelectors } from "@shared/store/";
import { CreateUserDto, UserFormShape } from "@containers/User/interfaces/UserForm.interface";

export const USER_STATUS_OPTIONS: Option[] = [
  { value: "Active", label: "Active" },
  { value: "Archived", label: "Archived" },
  { value: "Inactive", label: "Inactive" },
];

export const USER_CONNECTION_OPTIONS: Option[] = [
  { value: "Direct Contractor", label: "Direct Contractor" },
  { value: "Employee", label: "Employee" },
  { value: "Agency", label: "Agency" },
];

export const USER_TYPE_EMPLOYMENT_OPTIONS: Option[] = [
  { value: TYPE_EMPLOYMENT.FULL_TIME_JOB, label: TYPE_EMPLOYMENT.FULL_TIME_JOB },
  { value: TYPE_EMPLOYMENT.PART_TIME_JOB, label: TYPE_EMPLOYMENT.PART_TIME_JOB },
];

const optionValidationSchema = z.object(
  {
    value: z.string().min(1, FORM_ERROR_MESSAGES.REQUIRED),
    label: z.string().min(1, FORM_ERROR_MESSAGES.REQUIRED),
  },
  { error: FORM_ERROR_MESSAGES.REQUIRED },
);

/** The year of a `yyyy-mm-dd` date-input value, read from the text so the time zone cannot shift it. */
const getYear = (date: string) => Number(date.split("-")[0]);

/**
 * Form rules. A blank text field reports only "required" (the `min(1)` comes first). `used_pto` has no rule:
 * a cleared field is sent as an empty string.
 */
export const validationSchema = z.object({
  first_name: z.string().min(1, FORM_ERROR_MESSAGES.REQUIRED).max(50, `First name ${FORM_ERROR_MESSAGES.LONG}`),
  last_name: z.string().min(1, FORM_ERROR_MESSAGES.REQUIRED).max(50, `Last name ${FORM_ERROR_MESSAGES.LONG}`),
  email: z
    .string()
    .min(1, FORM_ERROR_MESSAGES.REQUIRED)
    .regex(IS_VALID_EMAIL, FORM_ERROR_MESSAGES.EMAIL)
    .max(150, `The email ${FORM_ERROR_MESSAGES.LONG}`),
  permission: optionValidationSchema,
  job_title: z.string().min(1, FORM_ERROR_MESSAGES.REQUIRED).max(50, `Job title ${FORM_ERROR_MESSAGES.LONG}`),
  team: optionValidationSchema,
  type_employment: z.string({ error: FORM_ERROR_MESSAGES.REQUIRED }).min(1, FORM_ERROR_MESSAGES.REQUIRED),
  connection: z.string({ error: FORM_ERROR_MESSAGES.REQUIRED }).min(1, FORM_ERROR_MESSAGES.REQUIRED),
  manager: optionValidationSchema,
  start_date: z
    .string()
    .min(1, FORM_ERROR_MESSAGES.REQUIRED)
    .refine((date) => date === "" || !Number.isNaN(new Date(date).getTime()), {
      error: (issue) =>
        `start_date must be a \`date\` type, but the final value was: \`Invalid Date\` (cast from the value \`"${String(issue.input)}"\`).`,
    })
    .refine((date) => {
      const year = getYear(date);
      return !year || year >= 2000;
    }, "Please specify valid date"),
  used_pto: z.union([z.number(), z.literal("")]),
}) satisfies z.ZodType<UserFormShape>;

export type UserFormValues = z.output<typeof validationSchema>;

export const getTeamInitValues = (user: User | null) => {
  if (!user || !user.team) return null;

  return {
    label: user.team.name,
    value: String(user.team.id),
  };
};

export const getRoleInitValue = (user: User | null) => {
  if (!user || !user.roles?.length) return null;

  return {
    label: `${user.roles[0].name}`,
    value: String(user.roles[0].id),
  };
};

export const getManagerInitValue = (user: User | null) => {
  if (!user || !user.manager) return null;

  return {
    label: `${user.manager?.first_name} ${user.manager?.last_name}`,
    value: String(user.manager.id),
  };
};

/** Form values for a user; `null` gives the create-mode defaults. `used_pto` arrives from a DECIMAL column, so it is coerced to a number. */
export const getInitValues = (user: User | null): UserFormShape => ({
  first_name: user?.first_name || "",
  last_name: user?.last_name || "",
  email: user?.login || "",
  job_title: user?.job_title || "",
  permission: getRoleInitValue(user),
  team: getTeamInitValues(user),
  type_employment: user?.type_employment || null,
  connection: user?.connection || USER_CONNECTION_OPTIONS[0].value,
  manager: getManagerInitValue(user),
  start_date: user?.start_date ? new Date(user.start_date).toISOString().substring(0, 10) : "",
  used_pto: Number(user?.used_pto) || 0,
});

/** Maps the parsed form output to the create/update request body. */
export const prepareSubmitValue = (values: UserFormValues, user: User | null): CreateUserDto => {
  const { manager, email, team, permission, start_date, ...rest } = values;
  return {
    ...rest,
    manager_id: Number(manager.value),
    login: email,
    status: user?.status || USER_STATUS.ACTIVE,
    team_id: Number(team.value),
    permission_id: Number(permission.value),
    start_date: new Date(start_date),
  };
};

export const handlers = {
  manager: {
    selectData: sharedSelectors.getUsers,
    loadingAction: sharedActions.getUsers,
    prepareOptionFunction: (u: User): Option => {
      return { label: `${u.first_name} ${u.last_name}`, value: String(u.id) };
    },
    getData: (search?: string) => {
      return sharedActions.getUsers.request({
        page: 0,
        limit: 500,
        search: search || "",
        status: USER_STATUS.ACTIVE,
      });
    },
    onSort: () => {
      return (a: Option, b: Option) => {
        return a.label.localeCompare(b.label);
      };
    },
  },
  roles: {
    selectData: sharedSelectors.getRoles,
    loadingAction: sharedActions.getRoles,
    prepareOptionFunction: (item: Role): Option => {
      return { label: item.name, value: String(item.id) };
    },
    getData: () => {
      return sharedActions.getRoles.request();
    },
  },
};
